import type { CANotificationItem, CANotificationType } from '@ds/interfaces'
import { NotificationService } from '@ds/services/NotificationService'
import { DELAY } from '@vuejs/helpers/_constants'
import { formatDistanceToNow } from 'date-fns'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { ErrorType, SuccessResponseType } from '@/javascript/interfaces'

import api from '../services/api'
import type {
	UserNotification,
	UserNotificationCollection,
	UserNotificationsResponse,
} from '../services/userNotifications'
import { getUserNotifications } from '../services/userNotifications'

export interface ExpandedNotification extends UserNotification {
	distanceToNow?: string
	read?: boolean
}

export const useNotificationStore = defineStore('notification', () => {
	// State
	const notifications = ref<ExpandedNotification[]>([])

	// Getters (Computed)
	const getNotificationCount = computed((): number => {
		return notifications.value.length
	})

	const hasUnreadNotifications = computed((): boolean => {
		return notifications.value.some(notification => !notification.read)
	})

	// Actions
	const getAPIErrorMessage = (
		error: ErrorType,
		defaultErrorMessage = 'An error occurred, please try again'
	): string[] | string => {
		return (
			error.response?.data?.message ||
			error.message ||
			error.responseJSON?.message ||
			defaultErrorMessage
		)
	}

	const getAPISuccessMessage = (
		response: SuccessResponseType,
		defaultSuccessMessage = 'Successfully completed'
	): string => {
		const data = response.data
		const isDataObject = typeof data === 'object' && data !== null
		const isDataString = typeof data === 'string'

		const message =
			isDataObject && data.success ||
			response.responseText ||
			isDataObject && data.message ||
			response.message ||
			isDataObject && data.success_messages ||
			response.success ||
			response.responseJSON?.message ||
			isDataString && data ||
			defaultSuccessMessage

		return typeof message === 'string' ? message : defaultSuccessMessage
	}

	const isRead = (timestamp: string, agencyTimeStamp: string): boolean => {
		// If the notification is older than the read timestamp, it is considered read
		return new Date(timestamp) < new Date(agencyTimeStamp)
	}

	const loadNotifications = async (agencyId: number): Promise<void> => {
		const { notifications: notificationsData }: UserNotificationsResponse = await getUserNotifications(api, agencyId)

		if (!notificationsData) {
			notifications.value = []

			return
		}

		const keys = Object.keys(notificationsData) as (keyof UserNotificationCollection)[]

		if (keys && keys.length === 0) {
			notifications.value = []

			return
		}

		const storedTimestamps = localStorage.getItem('notificationReadTimestamp')
		let agencyTimeStamp: string | null = null

		if (storedTimestamps) {
			const storedTimeStampsHash = JSON.parse(storedTimestamps) as Record<string, string>

			agencyTimeStamp = storedTimeStampsHash[agencyId]
		}

		// Flatten the notifications into a single array of UserNotification objects
		notifications.value = keys.reduce((acc: ExpandedNotification[], key) => {
			const list = notificationsData[key]

			if (!list) {
				return acc
			}

			return acc.concat(list.map(notification => {
				return {
					...notification,
					distanceToNow: formatDistanceToNow(new Date(notification.timestamp), { addSuffix: true }),
					read: !agencyTimeStamp ? false : isRead(notification.timestamp, agencyTimeStamp),
				}
			}))
		}, [])
	}

	const markNotificationsAsRead = (): void => {
		notifications.value.forEach(notification => {
			notification.read = true
		})
	}

	const notify = (notification: CANotificationItem): void => {
		if (!notification.message) {
			return
		}

		switch (notification.type) {
			case 'error':
				console.error(notification.message)
				NotificationService.methods.error({
					...notification,
					duration: notification.duration ?? DELAY.FIVE_SECONDS,
				})
				break

			case 'info':
				NotificationService.methods.info({
					...notification,
					duration: notification.duration ?? DELAY.FIVE_SECONDS,
				})
				break

			case 'success':
				NotificationService.methods.success({
					...notification,
					duration: notification.duration ?? DELAY.FIVE_SECONDS,
				})
				break

			default:
				NotificationService.methods.default({
					...notification,
					duration: notification.duration ?? DELAY.FIVE_SECONDS,
				})
				break
		}
	}

	const onHandleErrorMessages = ({
		duration,
		error,
	}: {
		duration?: number
		error: ErrorType
	}): void => {
		if (!!error && !!error.name && error.name === 'SecurityError') {
			notify({
				duration,
				message: error.message ?? 'Security Error',
				type: 'error',
			})

			return
		}

		const errorMessages =
			error.data?.error ??
			error.response?.data?.error ??
			error.response?.data?.errors ??
			error.response?.data?.messages ??
			error.response?.data?.message ??
			error.response?.error ??
			error.response?.errors ??
			error.data?.error_messages ??
			error.responseJSON?.message ??
			error.message ??
			error.json ??
			'An error occurred. Please try again.'

		if (typeof errorMessages === 'string') {
			notify({ message: errorMessages, type: 'error' })
		} else if (Array.isArray(errorMessages)) {
			for (const issue of errorMessages) {
				notify({ message: issue, type: 'error' })
			}
		} else if (typeof errorMessages === 'object') {
			for (const element in errorMessages) {
				if (
					Object.prototype.hasOwnProperty.call(errorMessages, element) &&
					errorMessages[element] &&
					Array.isArray(errorMessages[element])
				) {
					for (const issue of errorMessages[element]) {
						notify({ message: issue, type: 'error' })
					}
				}
			}
		}
	}

	const onHandleSuccessMessages = ({
		duration,
		message,
		response,
		type = 'success',
	}: {
		duration?: number
		message?: string
		response?: SuccessResponseType
		type?: CANotificationType
	}): void => {
		if (message) {
			notify({
				duration,
				message,
				type,
			})
		} else if (response) {
			const data = response.data
			const isDataObject = typeof data === 'object' && data !== null

			const successes =
				(isDataObject && data.success) ??
				response.responseText ??
				(isDataObject && data.message) ??
				response.message ??
				(isDataObject && data.success_messages) ??
				response.success ??
				response.responseJSON?.message ??
				data ??
				'Successfully completed!'

			const errorMessage = response.error

			if (typeof successes === 'string') {
				notify({
					duration,
					message: successes,
					type,
				})
			} else if (Array.isArray(successes)) {
				successes.forEach((msg: string) =>
					notify({
						duration,
						message: msg,
						type,
					})
				)
			}

			if (errorMessage) {
				notify({
					duration,
					message: errorMessage,
					type: 'error',
				})
			}
		}
	}

	const onHandleTextMessageResponseError = ({ response }: {
		response?: {
			data?: {
				error?: string
				error_messages?: string[] | string
			}
			error?: string[] | string
		}
	}): void => {
		const errors = response?.data?.error_messages ?? response?.data?.error ?? response?.error

		if (typeof errors === 'string') {
			notify({ message: errors, type: 'error' })
		} else {
			!!errors && errors.map((message: string) => notify({ message, type: 'error' }))
		}
	}

	const updateLocalTimestamp = (agencyId: string): void => {
		const storedTimestamps = localStorage.getItem('notificationReadTimestamp')

		if (storedTimestamps) {
			const storedTimeStampsHash = JSON.parse(storedTimestamps) as Record<string, string>

			storedTimeStampsHash[agencyId] = new Date().toISOString()
			localStorage.setItem('notificationReadTimestamp', JSON.stringify(storedTimeStampsHash))
		} else {
			localStorage.setItem('notificationReadTimestamp', JSON.stringify({ [agencyId]: new Date().toISOString() }))
		}
	}

	return {
		// State
		notifications,

		// Getters
		getNotificationCount,
		hasUnreadNotifications,

		// Actions
		getAPIErrorMessage,
		getAPISuccessMessage,
		isRead,
		loadNotifications,
		markNotificationsAsRead,
		notify,
		onHandleErrorMessages,
		onHandleSuccessMessages,
		onHandleTextMessageResponseError,
		updateLocalTimestamp,
	}
})
